import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useGetIndividualReservationQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { formatCurrencyShort } from '../../../../shared/utils';
import ExpenseModal from './ExpenseModal';
import moment from 'moment';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { useSearchParams } from 'react-router';
import { theme } from '../../../../shared/theme';
import GenericTable from '../../../shared/GenericTable';
import { MRT_ColumnDef } from 'mantine-react-table';

export default function ReservationExpenseClaims(params: {
    bookingId: number;
    expenses: {
        id: string;
        name: string;
        description: string;
        amount: number;
        date: Date;
        createdBy: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
        bookingId: number;
        attachments: [
            {
                uuid: string;
                src: string;
                name: string;
            },
        ];
        module: {
            id: number;
            name: string;
            type: string;
        };
        owner: {
            id: number;
            name: string;
        };
        guest: {
            id: number;
            name: string;
        };
        billId: number;
        invoiceId: number;
        hostPaidAt: Date;
        isDraft?: boolean;
    }[];
}) {
    const { bookingId, expenses } = params;

    const { data: getReservation } = useGetIndividualReservationQuery(
        `bookings/${bookingId}`,
    );

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const fullScreen = useMediaQuery('(max-width: 600px)');

    const [searchParams, setSearchParams] = useSearchParams();

    const [edtingExpense, setEditingExpense] = useState(false);
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [showExpenseModal, setShowExpenseModal] = useState(false);

    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('selectedExpense');
            return next;
        });
    };

    const handleOpenExpenseModal = () => {
        setEditingExpense(false);
        setCurrentIndex(null);
        setShowExpenseModal(true);
    };

    const handleCloseExpenseModal = () => {
        setShowExpenseModal(false);
        setEditingExpense(false);
        setCurrentIndex(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('selectedExpense');
            return next;
        });
    };

    const openExpenseById = (id: string) => {
        const index =
            expenses?.findIndex((e) => String(e?.id) === String(id)) ?? -1;

        setEditingExpense(true);
        setCurrentIndex(index >= 0 ? index : null);

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('selectedExpense', String(id));
            return next;
        });

        setShowExpenseModal(true);
    };

    useEffect(() => {
        const id = searchParams.get('selectedExpense');
        if (!id) return;

        setOpen(true);

        const index =
            expenses?.findIndex((e) => String(e?.id) === String(id)) ?? -1;

        setEditingExpense(true);
        setCurrentIndex(index >= 0 ? index : null);
        setShowExpenseModal(true);
    }, [searchParams]);

    const totalExpenseAmount = useMemo(() => {
        return (expenses || []).reduce(
            (sum, exp) => sum + (exp?.amount || 0),
            0,
        );
    }, [expenses]);

    const totalExpenseAmountFormatted = useMemo(() => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
        }).format(totalExpenseAmount);
    }, [totalExpenseAmount]);

    const renderStatusChip = (exp: any) => {
        const label = exp?.hostPaidAt
            ? 'Paid'
            : exp?.isDraft
              ? 'Draft'
              : 'Unpaid';

        const chipColor = exp?.hostPaidAt
            ? 'success'
            : exp?.isDraft
              ? 'default'
              : 'error';

        return (
            <Chip
                label={label.toUpperCase()}
                color={chipColor}
                sx={{
                    height: 'auto',
                    fontSize: '10px',
                    fontWeight: 700,
                }}
            />
        );
    };

    const columns: MRT_ColumnDef<any>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'Id',
                size: 60,
                minSize: 50,
                maxSize: 70,
                grow: false,
                Cell: ({ cell }) => `#${cell.getValue()}`,
            },
            {
                accessorKey: 'name',
                header: 'Expense',
                size: isIpad ? 160 : 240,
                Cell: ({ cell }) => cell.getValue<string>(),
            },
            {
                accessorKey: 'date',
                header: 'Date',
                size: isMobile ? 140 : 90,
                grow: false,
                Cell: ({ cell }) =>
                    moment(cell.getValue<Date>()).format('DD/MM/YYYY'),
            },
            {
                accessorKey: 'bookingId',
                header: 'Reservation',
                size: 320,
                Cell: ({ row }) =>
                    `Reservation #${row.original?.bookingId} - ${row.original?.module?.name}`,
            },
            {
                accessorKey: 'guest.name',
                header: 'Guest',
                size: isIpad ? 150 : 200,
                accessorFn: (row) => row?.guest?.name ?? row?.guest?.owner,
            },
            {
                accessorKey: 'amount',
                header: 'Amount (inc GST)',
                size: 140,
                grow: false,
                Cell: ({ cell }) =>
                    formatCurrencyShort(cell.getValue<number>() ?? 0),
            },
            {
                accessorKey: 'hostPaidAt',
                header: 'Status',
                size: 120,
                grow: false,
                Cell: ({ row }) => renderStatusChip(row.original),
            },
        ],
        [isIpad, isMobile],
    );

    return (
        <>
            <Box
                onClick={handleOpen}
                role='button'
                tabIndex={0}
                sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                        borderColor: theme.palette.info.main,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.neutral.main,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <ReceiptLongIcon
                            fontSize='small'
                            sx={{ opacity: 0.75 }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            variant='body3'
                            sx={{
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                            }}
                        >
                            Expense claims ({expenses?.length || 0})
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                                lineHeight: 1.2,
                            }}
                        >
                            Total: {totalExpenseAmountFormatted}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Open
                </Typography>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                maxWidth='lg'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: fullScreen ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        height: fullScreen ? '100dvh' : 'auto',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        position: fullScreen ? 'sticky' : 'relative',
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        Expense claims
                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                            }}
                        >
                            Submit expense claims linked to this reservation and
                            receive reimbursement for any costs incurred.
                        </Typography>
                    </Box>

                    <IconButton onClick={handleClose} size='small'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            p: 1.75,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    letterSpacing: 0.3,
                                    fontSize: 12,
                                    textTransform: 'uppercase',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                What are expense claims?
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.45,
                                }}
                            >
                                Expense claims are requests for reimbursement of
                                costs incurred while providing a service or
                                completing a task.
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.45,
                                }}
                            >
                                Guests will see the expense name, description,
                                date and attachments under Total Charges.
                            </Typography>
                        </Box>

                        <Button
                            variant='outlined'
                            sx={{
                                height: 44,
                                alignSelf: isMobile ? 'stretch' : 'flex-start',
                                minWidth: isMobile ? '100%' : 160,
                                borderRadius: 2,
                            }}
                            fullWidth={isMobile}
                            onClick={handleOpenExpenseModal}
                        >
                            Add expense
                        </Button>
                    </Box>

                    {isMobile ? (
                        <Stack spacing={1.25}>
                            {(expenses ?? []).length ? (
                                (expenses ?? []).map((exp) => (
                                    <Box
                                        key={exp.id}
                                        onClick={() =>
                                            openExpenseById(String(exp.id))
                                        }
                                        role='button'
                                        tabIndex={0}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 900,
                                                    fontSize: 14,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {exp.name ||
                                                    `Expense #${exp.id}`}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    mt: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .text.secondary,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {moment(exp.date).format(
                                                        'DD/MM/YYYY',
                                                    )}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .text.secondary,
                                                    }}
                                                >
                                                    •
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        color: theme.palette
                                                            .text.primary,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {formatCurrencyShort(
                                                        exp.amount ?? 0,
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {renderStatusChip(exp)}
                                            <ChevronRightIcon
                                                sx={{ opacity: 0.6 }}
                                            />
                                        </Box>
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{ px: 0.5, py: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No expense claims yet.
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Box sx={{ width: '100%', paddingTop: '20px' }}>
                            <GenericTable
                                columns={columns}
                                data={expenses ?? []}
                                tableName='Reservation-Expense-Claims'
                                loading={!expenses}
                                cursor='pointer'
                                onRowClick={(row) =>
                                    openExpenseById(String(row?.id))
                                }
                                enableRowSelection={false}
                                enableMultiRowSelection={false}
                                enableSelectAll={false}
                            />

                            {!expenses?.length && (
                                <Box sx={{ px: 2, py: 2 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No expense claims yet.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        position: fullScreen ? 'sticky' : 'relative',
                        bottom: 0,
                        zIndex: 2,
                    }}
                >
                    <Typography
                        variant='body3'
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 900,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Total: {totalExpenseAmountFormatted}
                    </Typography>

                    <Button
                        variant='outlined'
                        onClick={handleClose}
                        sx={{ borderRadius: 2, minHeight: 44 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ExpenseModal
                open={showExpenseModal}
                onClose={handleCloseExpenseModal}
                expenses={expenses}
                reservation={getReservation}
                isEditing={edtingExpense}
                index={currentIndex}
            />
        </>
    );
}
